<template>
  <div class="section-container">
    <h2 class="section-title">表单录入区域</h2>

    <div class="form-container">
      <!-- 总金额录入（simple 模式） -->
      <div v-if="mode !== 'discount'" class="form-row">
        <TotalAmountInput
          v-model="localForm.totalAmount"
        />
      </div>

      <!-- Discount 模式：总金额自动计算展示 -->
      <div v-if="mode === 'discount'" class="form-row">
        <div class="form-col">
          <div class="form-item">
            <label class="form-label">
              <span class="label-text">订单总金额（自动计算）：</span>
              <span class="label-tip">💰</span>
            </label>
            <span class="auto-total">{{ computedTotal }} 元</span>
          </div>
        </div>
      </div>

      <!-- 参与人管理 -->
      <div class="form-row">
        <PersonManager
          v-model="localForm.personList"
          :mode="mode"
        />
      </div>

      <!-- 优惠规则配置（discount 模式） -->
      <div v-if="mode === 'discount'" class="form-row">
        <DiscountRuleConfig
          v-model="localForm.discountRules"
          :person-list="localForm.personList"
        />
      </div>

      <!-- 代付项管理 -->
      <div class="form-row">
        <ProxyManager
          v-model="localForm.proxyList"
        />
      </div>
    </div>

    <!-- 凑单建议（discount 模式） -->
    <SuggestionPanel
      :suggestion-data="suggestionData"
      :show="mode === 'discount'"
    />

    <!-- 按钮 -->
    <div class="button-group">
      <button @click="calculate" class="btn btn-calculate">开始计算</button>
      <button @click="reset" class="btn btn-reset">重置表单</button>
    </div>
  </div>
</template>

<script>
import TotalAmountInput from './TotalAmountInput.vue';
import PersonManager from './PersonManager.vue';
import ProxyManager from './ProxyManager.vue';
import DiscountRuleConfig from './DiscountRuleConfig.vue';
import SuggestionPanel from './SuggestionPanel.vue';

export default {
  name: 'FormInputSection',
  components: {
    TotalAmountInput,
    PersonManager,
    ProxyManager,
    DiscountRuleConfig,
    SuggestionPanel
  },
  props: {
    initialForm: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      default: 'simple'
    },
    suggestionData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      localForm: {
        totalAmount: 0,
        personList: [{ name: '', payAmount: 0 }],
        proxyList: [{ proxyName: '', beProxyName: '', proxyAmount: 0 }],
        discountRules: {
          enabled: false,
          thresholds: [{ threshold: 0, discount: 0 }],
          coupons: [{ name: '', amount: 0, threshold: 0, applicablePerson: '' }],
          strategy: 'proportional',
          organizer: ''
        }
      }
    }
  },
  computed: {
    computedTotal() {
      return this.localForm.personList.reduce((sum, p) => {
        const itemsTotal = (p.items || []).reduce((s, it) => s + (it.price || 0), 0)
        return sum + itemsTotal
      }, 0).toFixed(2)
    }
  },
  watch: {
    initialForm: {
      handler(newVal) {
        const newValStr = JSON.stringify(newVal)
        const localFormStr = JSON.stringify(this.localForm)
        if (newValStr !== localFormStr) {
          this.localForm = JSON.parse(newValStr)
        }
      },
      deep: true,
      immediate: true
    },
    localForm: {
      handler(newVal) {
        this.$emit('form-change', JSON.parse(JSON.stringify(newVal)))
      },
      deep: true
    }
  },
  methods: {
    reset() {
      this.localForm = {
        totalAmount: 0,
        personList: this.mode === 'discount'
          ? [{ name: '', payAmount: 0, items: [{ itemName: '', price: 0 }] }, { name: '', payAmount: 0, items: [{ itemName: '', price: 0 }] }]
          : [{ name: '', payAmount: 0 }, { name: '', payAmount: 0 }],
        proxyList: [{ proxyName: '', beProxyName: '', proxyAmount: 0 }],
        discountRules: {
          enabled: false,
          thresholds: [{ threshold: 0, discount: 0 }],
          coupons: [{ name: '', amount: 0, threshold: 0, applicablePerson: '' }],
          strategy: 'proportional',
          organizer: ''
        }
      }
      this.$emit('reset')
    },

    calculate() {
      this.$emit('calculate')
    }
  }
}
</script>

<style scoped>
.form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.form-col {
  flex: 1;
  min-width: 200px;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

.auto-total {
  font-size: 24px;
  font-weight: 700;
  color: #667eea;
}

.form-item {
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
  font-size: 14px;
}

.label-text {
  font-weight: 600;
  color: #4a5568;
}

.label-tip {
  font-size: 18px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.button-group {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.btn-calculate {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 16px;
  padding: 14px 32px;
  font-weight: 700;
  min-width: 150px;
}

.btn-calculate:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.3);
  transform: translateY(-3px);
}

.btn-reset {
  background: #e2e8f0;
  color: #4a5568;
  font-size: 16px;
  padding: 14px 32px;
  font-weight: 600;
  min-width: 150px;
}

.btn-reset:hover {
  background: #cbd5e0;
  box-shadow: 0 4px 12px rgba(148, 163, 184, 0.25);
}

@media (max-width: 750px) {
  .form-container {
    gap: 16px;
  }

  .form-row {
    flex-direction: column;
    gap: 16px;
  }

  .form-col {
    min-width: 100%;
  }

  .section-title {
    font-size: 16px;
    margin-bottom: 16px;
  }

  .button-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 15px;
    padding-top: 12px;
  }

  .btn {
    width: 100%;
    box-sizing: border-box;
    font-size: 13px;
    padding: 10px 12px;
  }

  .btn-calculate {
    padding: 10px 16px;
    font-size: 14px;
  }
}

@media (max-width: 320px) {
  .section-title {
    font-size: 16px;
  }

  .btn {
    font-size: 13px;
    padding: 10px 16px;
  }
}
</style>
